/**
 * Published Prototype Page (Web)
 * 
 * Displays a published prototype using the PublishedPrototypeTemplate.
 * Fetches prototype data from the API and renders it.
 * 
 * Route: /p/*
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { prototyperService } from '@ideoz/core';
import type { PrototyperBySlugResponse } from '@ideoz/core';
import { PublishedPrototypeTemplate } from '@ideoz/ui';

export function PublishedPrototype() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract slug from pathname (everything after /p/)
  const slug = location.pathname.replace(/^\/p\//, '');
  const [prototyperData, setPrototyperData] = useState<PrototyperBySlugResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrototype = async () => {
      if (!slug) {
        setError('No slug provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Fetching prototype with slug:', slug);
        const data = await prototyperService.getPrototyperBySlug(slug);
        console.log('Prototype data received:', data);
        
        setPrototyperData(data);
        
        // Set page title to the prototype header
        if (data.header) {
          document.title = data.header;
        }
      } catch (err) {
        console.error('Error fetching prototype:', err);
        setError(err instanceof Error ? err.message : 'Failed to load prototype');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrototype();
  }, [slug]);

  const handleRetry = () => {
    // Force reload by updating state
    setIsLoading(true);
    setError(null);
    window.location.reload();
  };

  return (
    <PublishedPrototypeTemplate
      isLoading={isLoading}
      error={error}
      demoUrl={prototyperData?.demoUrl || null}
      title={prototyperData?.header || 'Published Prototype'}
      onNavigateHome={() => navigate('/')}
      onRetry={handleRetry}
    />
  );
}

export default PublishedPrototype;
